import { useCallback, useState } from 'react';
import { Input, InputProps, Popover, PopoverProps } from 'antd';
import { Calendar } from '@hassanmojab/react-modern-calendar-datepicker';
import moment, { Moment } from 'jalali-moment';
import { CalendarOutlined } from '@ant-design/icons';
import { useThemeToken } from '@/hooks';

export interface CalendarDate {
  year: number;
  month: number;
  day: number;
  str: string;
  strEn: string;
  iso: string;
  moment: Moment;
}

export interface InputDateProps extends InputProps {
  defaultDate?: CalendarDate;
  popoverProps?: Omit<PopoverProps, 'content' | 'open' | 'onOpenChange'>
  closeOnChange?: boolean;
  valueFormatter?: (date: CalendarDate) => string;
  onChange?: (date: CalendarDate & any) => void;
}

export function InputCalendar({
  popoverProps = {},
  // onDateChange = undefined,
  defaultDate = undefined,
  closeOnChange = true,
  valueFormatter = (date) => !date ? undefined : `${date.year}/${twoDigit(date.month)}/${twoDigit(date.day)}`,
  onChange = undefined,
  ...rest
}: InputDateProps) {

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(defaultDate);

  const { colorPrimary } = useThemeToken();

  const _onChange = useCallback((date: CalendarDate) => {
    const str = `${date.year}-${twoDigit(date.month)}-${twoDigit(date.day)}`;
    const momentDate = moment.from(str, 'fa', 'jYYYY-jMM-jDD');
    const strEn = momentDate.clone().locale('en').format('YYYY-MM-DD');
    const final = {
      ...date,
      str,
      strEn,
      iso: momentDate.toISOString(),
      moment: momentDate
    };
    setSelectedDate(final);
    if (onChange) {
      onChange(final);
    }
    if (closeOnChange) {
      setOpen(false);
    }
  }, [onChange, setSelectedDate, setOpen, closeOnChange]);

  return (
    <Popover
      open={open}
      trigger={['click']}
      onOpenChange={setOpen}
      content={
        <Calendar
          locale="fa"
          value={selectedDate}
          onChange={_onChange}
          shouldHighlightWeekends
        />
      }
      rootClassName="!p-0"
      overlayInnerStyle={{ padding: 0 }}
      {...popoverProps}
    >
      <Input
        readOnly
        suffix={<CalendarOutlined onClick={() => setOpen(o => !o)} style={{ color: colorPrimary }} />}
        {...rest}
        value={valueFormatter(selectedDate)}
      />
    </Popover>
  );
}

export default InputCalendar;

const twoDigit = (num: string | number) => String(num).length === 1 ? ('0' + num) : num;
